import React, { useCallback, useEffect, useState, useMemo } from "react";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import { formatTimeAgo } from "views_shared/app/javascript/utils/dateFormatters";

const SUPABASE_URL = () => globalThis.Qwoted.SUPABASE_API_EXTERNAL_URL;

const FIELD_LABELS = {
  title: "title",
  details: "description",
  equipment_notes: "equipment notes",
  links: "links",
  logistics_details: "logistics",
  media_outlet_id: "publication",
};

const EDIT_EVENT_TYPES = new Set(["admin.request_edited", "automation.reporter_edited_request"]);

function hasChanges(item) {
  return (
    EDIT_EVENT_TYPES.has(item.event_type) && item.metadata?.changes && Object.keys(item.metadata.changes).length > 0
  );
}

function formatValue(val) {
  if (val == null || val === "") return "(empty)";
  if (Array.isArray(val)) return val.length === 0 ? "(empty)" : val.join(", ");
  return String(val);
}

function ChangeDiff({ changes }) {
  return (
    <div className="mt-2 small" style={{ backgroundColor: "#f8f9fa", borderRadius: "4px", padding: "8px" }}>
      {Object.entries(changes).map(([field, vals]) => {
        const label = FIELD_LABELS[field] || field.replace(/_/g, " ");
        const [oldVal, newVal] = Array.isArray(vals) ? vals : [null, vals];
        return (
          <div key={field} className="mb-2">
            <div className="fw-bold text-capitalize" style={{ fontSize: "0.8em", color: "#6c757d" }}>
              {label}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" }}>
              <div
                style={{ backgroundColor: "#fdd", borderRadius: "3px", padding: "3px 6px", wordBreak: "break-word" }}
              >
                <span style={{ color: "#999", fontSize: "0.75em" }}>was: </span>
                {formatValue(oldVal)}
              </div>
              <div
                style={{ backgroundColor: "#dfd", borderRadius: "3px", padding: "3px 6px", wordBreak: "break-word" }}
              >
                <span style={{ color: "#999", fontSize: "0.75em" }}>now: </span>
                {formatValue(newVal)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function entityLabel(entityType) {
  if (entityType === "Interact::Request") return "Request";
  if (entityType === "Id::MediaOutlet") return "Outlet";
  if (entityType === "Id::Profile::MediaProfile") return "Profile";
  if (entityType === "Id::Profile::PrProfile") return "Profile";
  if (entityType === "Id::Profile::ExpertProfile") return "Profile";
  if (entityType === "Account::User") return "User";
  if (entityType === "Admin::UserVerificationDecision") return "Signup check";
  return entityType;
}

const USER_VERIFICATION_OUTCOMES = { no_action: "No action", suspicious: "Suspicious", under_audit: "Under audit" };

const USER_VERIFICATION_VERDICTS = {
  audit_lifted: "Audit lifted",
  audit_kept: "Audit kept",
  disabled: "User disabled",
  vetted: "Vetted",
  set_under_audit: "Set under audit",
};

function describeUserVerificationDecided(metadata) {
  const outcome = USER_VERIFICATION_OUTCOMES[metadata?.outcome] || metadata?.outcome;
  const rules = (metadata?.matched_rules || []).map((rule) => rule.value).join(", ");
  const details = [rules, metadata?.reason?.replace(/_/g, " ")].filter(Boolean).join("; ");
  return `Automated: ${outcome}${details ? ` (${details})` : ""}`;
}

function describeEvent(entry) {
  const { event_type: eventType, metadata, actor_name: actorName } = entry;

  switch (eventType) {
    case "automation.user_verification_decided":
      return describeUserVerificationDecided(metadata);
    case "admin.user_verification_reviewed":
      return `${USER_VERIFICATION_VERDICTS[metadata?.verdict] || "Reviewed"} by ${actorName || "admin"}`;
    case "automation.request_processed": {
      const decision = metadata?.decision;
      if (decision === "auto_approved") return "Auto-approved";
      if (decision === "auto_approved_feed") return "Auto-approved + Feed";
      if (decision === "auto_approved_feed_notify") return "Auto-approved + Feed & Notified";
      if (decision === "routed_to_review") return "Routed to manual review";
      if (decision === "ghosted" || decision === "auto_ghosted") return "Auto-ghosted";
      return `Automation: ${decision || "processed"}`;
    }
    case "admin.request_approved": {
      const decision = metadata?.decision;
      if (decision === "approved_feed_notify") return `Approved + Feed & Notified by ${actorName || "admin"}`;
      if (decision === "approved_feed") return `Approved + Feed by ${actorName || "admin"}`;
      return `Approved by ${actorName || "admin"}`;
    }
    case "admin.request_ghosted":
      return `Ghosted by ${actorName || "admin"}`;
    case "admin.request_unghosted":
      return `Unghosted by ${actorName || "admin"}`;
    case "config.changed": {
      const changes = metadata?.changes;
      if (!changes) return "Configuration changed";
      return Object.entries(changes)
        .map(([field, [oldVal, newVal]]) => {
          const label = field.replace(/_/g, " ");
          if (typeof newVal === "boolean") {
            return `${label} ${newVal ? "enabled" : "disabled"}`;
          }
          return `${label} changed from ${oldVal || "none"} to ${newVal || "none"}`;
        })
        .join("; ");
    }
    case "admin.request_edited": {
      const changes = metadata?.changes;
      if (!changes) return `Edited by ${actorName || "admin"}`;
      const fields = Object.keys(changes)
        .map((f) => f.replace(/_/g, " "))
        .join(", ");
      return `${fields} edited by ${actorName || "admin"}`;
    }
    case "admin.request_assigned": {
      const prev = metadata?.previous_assignee_name;
      if (prev) return `Assigned to ${actorName || "admin"} (was ${prev})`;
      return `Assigned to ${actorName || "admin"}`;
    }
    case "admin.request_unassigned":
      return `Unassigned by ${actorName || "admin"}`;
    case "admin.request_claimed": {
      // Legacy event type — kept for backward compatibility with existing events
      const prev = metadata?.previous_assignee_name;
      if (prev) return `Assigned to ${actorName || "admin"} (was ${prev})`;
      return `Assigned to ${actorName || "admin"}`;
    }
    case "admin.request_unclaimed":
      return `Unassigned by ${actorName || "admin"}`;
    case "admin.post_edit_review_dismissed":
      return `Post-edit review dismissed by ${actorName || "admin"}`;
    case "admin.request_resubmitted":
      return `Resubmitted for auto-approval by ${actorName || "admin"}`;
    case "automation.reporter_edited_request": {
      const changes = metadata?.changes || {};
      const fields = Object.keys(changes);
      const fieldList = fields.map((f) => FIELD_LABELS[f] || f.replace(/_/g, " ")).join(", ");
      return `${fieldList} edited by ${actorName || "reporter"}`;
    }
    case "automation.content_moderation":
    case "automation.request_content_moderation":
      return null; // Shown separately in ContentModResults
    default:
      return eventType;
  }
}

function ActivityTimeline({ entityType, entityId, entities }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentBody, setCommentBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [expandedEdits, setExpandedEdits] = useState(new Set());

  const toggleEditExpand = useCallback((itemId) => {
    setExpandedEdits((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }, []);

  const entityList = useMemo(() => entities || [{ type: entityType, id: entityId }], [entities, entityType, entityId]);
  const primaryEntity = entityList[0];

  const fetchActivity = useCallback(async () => {
    try {
      const headers = {
        ...authenticatedHeadersHash(),
        "Content-Type": "application/json",
      };
      const base = `${SUPABASE_URL()}/rest/v1/activity`;

      let url;
      if (entityList.length === 1) {
        const ent = entityList[0];
        url = `${base}?entity_type=eq.${encodeURIComponent(ent.type)}&entity_id=eq.${ent.id}&order=created_at.desc`;
      } else {
        const orClauses = entityList
          .map((ent) => `and(entity_type.eq.${encodeURIComponent(ent.type)},entity_id.eq.${ent.id})`)
          .join(",");
        url = `${base}?or=(${orClauses})&order=created_at.desc`;
      }

      const res = await fetch(url, { headers });
      if (res.ok) {
        const data = await res.json();
        setItems(data);
      }
    } finally {
      setLoading(false);
    }
  }, [entityList]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  const handlePostComment = async () => {
    if (!commentBody.trim() || posting) return;

    setPosting(true);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const res = await fetch("/admin/api/comments", {
        method: "POST",
        headers: {
          ...authenticatedHeadersHash(),
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          comment: {
            commentable_type: primaryEntity.type,
            commentable_id: primaryEntity.id,
            body: commentBody.trim(),
          },
        }),
      });

      if (res.ok) {
        setCommentBody("");
        fetchActivity();
      }
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4 text-muted">
        <i className="fa fa-spinner fa-spin me-2" />
        Loading activity...
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header bg-white">
        <h6 className="mb-0">Activity</h6>
      </div>
      <div className="card-body">
        {/* Comment input */}
        <div className="d-flex gap-2 mb-4">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Add a comment..."
            value={commentBody}
            onChange={(event) => setCommentBody(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") handlePostComment();
            }}
            disabled={posting}
          />
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={handlePostComment}
            disabled={!commentBody.trim() || posting}
          >
            {posting ? "Posting..." : "Post"}
          </button>
        </div>

        {/* Timeline */}
        {items.length === 0 ? (
          <p className="text-muted small">No activity yet.</p>
        ) : (
          <div style={{ position: "relative", paddingLeft: "24px" }}>
            {/* Vertical line */}
            <div
              style={{
                position: "absolute",
                left: "7px",
                top: "8px",
                bottom: "8px",
                width: "2px",
                backgroundColor: "#dee2e6",
              }}
            />

            {items.map((item) => {
              const isComment = item.activity_type === "comment";
              const description = isComment ? null : describeEvent(item);

              // Skip content moderation events
              if (!isComment && description === null) return null;

              const showEntityPrefix = entityList.length > 1;
              const prefix = showEntityPrefix ? entityLabel(item.entity_type) : null;

              return (
                <div key={`${item.activity_type}-${item.id}`} className="mb-3" style={{ position: "relative" }}>
                  {/* Dot */}
                  <div
                    style={{
                      position: "absolute",
                      left: "-20px",
                      top: "6px",
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      backgroundColor: isComment ? "#6c757d" : "#0d6efd",
                      border: "2px solid white",
                    }}
                  />

                  {isComment ? (
                    <div className="p-2 rounded" style={{ backgroundColor: "#f8f9fa" }}>
                      <div className="d-flex justify-content-between align-items-start">
                        <strong className="small">
                          {prefix && <span className="text-muted me-1">[{prefix}]</span>}
                          {item.actor_name || "Admin"}
                        </strong>
                        <span className="text-muted small">{formatTimeAgo(item.created_at)}</span>
                      </div>
                      <p className="mb-0 small mt-1">{item.body}</p>
                    </div>
                  ) : (
                    <div>
                      <div className="d-flex justify-content-between align-items-start">
                        <span className="small">
                          {prefix && <strong className="text-muted me-1">[{prefix}]</strong>}
                          {description}
                          {hasChanges(item) && (
                            <>
                              {" "}
                              <button
                                type="button"
                                className="btn btn-link btn-sm text-decoration-none p-0 align-baseline"
                                onClick={() => toggleEditExpand(item.id)}
                              >
                                details {expandedEdits.has(item.id) ? "▲" : "▼"}
                              </button>
                            </>
                          )}
                        </span>
                        <span className="text-muted small">{formatTimeAgo(item.created_at)}</span>
                      </div>
                      {item.metadata?.reason && (
                        <span className="text-muted small d-block">Reason: {item.metadata.reason}</span>
                      )}
                      {hasChanges(item) && expandedEdits.has(item.id) && <ChangeDiff changes={item.metadata.changes} />}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default ActivityTimeline;
